import type { browserTracingIntegration } from '@sentry/react';

type BrowserTracingOptions = NonNullable<Parameters<typeof browserTracingIntegration>[0]>;
type ShouldCreateSpanForRequest = NonNullable<BrowserTracingOptions['shouldCreateSpanForRequest']>;

type IgnoredRequestRule = {
  host: RegExp;
  pathname: string;
};

/*
 * Analytics beacons fire on every page and would each get an http span and trace headers.
 * Only these exact collector endpoints are ignored; other requests to the same hosts keep their spans.
 */
const IGNORED_REQUESTS = {
  googleAnalytics: { host: /^(region1|www)\.google-analytics\.com$/, pathname: '/g/collect' },
  clarity: { host: /^(?:[a-z]|www)\.clarity\.ms$/, pathname: '/collect' },
} as const satisfies Record<string, IgnoredRequestRule>;

function parseRequestUrl(url: string): URL | undefined {
  try {
    // Relative URLs are the app's own requests; resolving them keeps them out of the rules above.
    return new URL(url, window.location.origin);
  } catch {
    return undefined;
  }
}

function matchesRule({ host, pathname }: URL, rule: IgnoredRequestRule): boolean {
  return rule.host.test(host) && rule.pathname === pathname;
}

export const shouldCreateSpanForRequest: ShouldCreateSpanForRequest = (url: string) => {
  const requestUrl: URL | undefined = parseRequestUrl(url);
  if (!requestUrl) {
    return true;
  }

  return !Object.values(IGNORED_REQUESTS).some((rule) => matchesRule(requestUrl, rule));
};
